import React, { useState, useEffect, useCallback } from 'react';
import { SiteData, PublishedSiteSnapshot } from '../../types';
import { useSite } from '../../context/SiteContext';
import { realtimeDbService as dbService } from '../../services/realtimeDbService';
import { isSiteDomainConnected, normalizeDomain } from '../../utils/security';
import {
  Globe,
  ShieldCheck,
  Lock,
  Clock,
  RotateCw,
  ExternalLink,
  X,
  Layers,
  AlertCircle,
  Info,
} from 'lucide-react';

export interface LivePreviewHeaderProps {
  site: SiteData;
  currentPath?: string;
  publishedSnapshot?: PublishedSiteSnapshot | null;
  onClose: () => void;
  onRefresh?: () => void;
}

export const LivePreviewHeader: React.FC<LivePreviewHeaderProps> = ({
  site,
  currentPath = '/',
  publishedSnapshot,
  onClose,
  onRefresh,
}) => {
  const { showToast } = useSite();

  const rawDomain = site.customDomain ? normalizeDomain(site.customDomain) : '';
  const [isConnected, setIsConnected] = useState<boolean>(() => isSiteDomainConnected(site));
  const [domainStatus, setDomainStatus] = useState<string>(site.domainStatus || 'PENDING_DNS');
  const [sslStatus, setSslStatus] = useState<string>(site.sslStatus || 'PENDING');
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);

  // Check custom domain status via Realtime Database and backend Firebase Hosting API
  const checkDomainLiveStatus = useCallback(async (isManualTrigger = false) => {
    if (!rawDomain) {
      setIsConnected(false);
      return;
    }

    if (isManualTrigger) {
      setIsChecking(true);
    }

    try {
      // 1. Check local RTDB mapping first for immediate fast evaluation
      const mapping = await dbService.getDomainMapping(rawDomain);
      if (mapping) {
        setDomainStatus(mapping.status);
        setSslStatus(mapping.sslStatus);
        const mappingConnected =
          (mapping.status === 'ACTIVE' || mapping.status === 'VERIFIED') &&
          mapping.sslStatus === 'ACTIVE';
        if (mappingConnected) {
          setIsConnected(true);
        }
      }

      // 2. Query authoritative Firebase Hosting status endpoint
      const res = await fetch(`/api/domains/status/${encodeURIComponent(rawDomain)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          const apiConnected =
            (data.status === 'CONNECTED' || data.hostState === 'HOST_ACTIVE') &&
            (data.sslStatus === 'SSL_ACTIVE' || data.sslStatus === 'ACTIVE');

          setIsConnected(apiConnected);
          setDomainStatus(data.status || 'PENDING_DNS');
          setSslStatus(data.sslStatus || 'PENDING');

          if (isManualTrigger) {
            if (apiConnected) {
              if (showToast) showToast(`${rawDomain} başarıyla aktif ve SSL güvencesinde.`, 'success');
            } else {
              if (showToast) showToast('Özel alan adı DNS/SSL provizyonu devam ediyor.', 'info');
            }
          }
          return;
        }
      }

      // Fallback check based on site entity
      setIsConnected(isSiteDomainConnected(site));
    } catch (err) {
      console.warn('[LivePreviewHeader] Domain status check failed, using cached state:', err);
      setIsConnected(isSiteDomainConnected(site));
    } finally {
      setIsChecking(false);
    }
  }, [rawDomain, site, showToast]);

  useEffect(() => {
    checkDomainLiveStatus(false);
  }, [checkDomainLiveStatus]);

  // Handle "Yeni Sekmede Aç" click per Rule 10 & Rule 14
  const handleOpenInNewTab = () => {
    const slug = site.slug || site.subdomain || 'site';
    const normalizedPath = currentPath === '/' ? '' : currentPath;
    const defaultSlugUrl = `https://www.adiosoft.com/${slug}${normalizedPath}`;

    // Has custom domain, but DNS or SSL approval is still in progress (PENDING_DNS / PENDING_SSL)
    if (rawDomain && !isConnected) {
      // Rule 14: User Information (Toast / Alert)
      const warningMessage =
        'Özel alan adınızın güvenlik (SSL) kurulumu devam ediyor (15-60 dk sürebilir). Bu süre zarfında sitenize Adiosoft bağlantınız üzerinden erişebilirsiniz.';

      if (showToast) {
        showToast(warningMessage, 'info');
      }
      setNoticeMessage(warningMessage);

      // Rule 10: Smart Routing: Open default working Adiosoft slug URL instead of breaking domain
      window.open(defaultSlugUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // Custom domain is verified and ACTIVE with SSL
    if (rawDomain && isConnected) {
      const customUrl = `https://${rawDomain}${normalizedPath}`;
      window.open(customUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // Default site without custom domain
    window.open(defaultSlugUrl, '_blank', 'noopener,noreferrer');
  };

  const slug = site.slug || site.subdomain || 'site';
  const normalizedPath = currentPath === '/' ? '' : currentPath;

  return (
    <div className="relative z-50 flex flex-col select-none">
      {/* Top Browser Simulator Bar */}
      <div className="h-14 bg-slate-900 border-b border-slate-800 px-4 flex items-center justify-between text-white">
        {/* Left: Window Dots & Title */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-full bg-rose-500 hover:bg-rose-600 transition cursor-pointer"
              onClick={onClose}
              title="Kapat"
            />
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
          </div>
          <div className="h-4 w-px bg-slate-800" />
          <span className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5 text-blue-400" />
            {site.name} — Canlı Önizleme
          </span>
          {publishedSnapshot && (
            <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-mono">
              <Layers className="w-2.5 h-2.5" />
              v{publishedSnapshot.versionId.substring(0, 7)}
            </span>
          )}
        </div>

        {/* Center: URL Bar simulator */}
        <div className="hidden md:flex items-center bg-slate-950 border border-slate-800 px-4 py-1.5 rounded-full text-xs text-slate-300 w-[26rem] max-w-lg justify-between shadow-inner">
          <div className="flex items-center gap-2 truncate">
            {rawDomain && isConnected ? (
              // Connected State: Green SSL Shield + Customer's Custom Domain
              <>
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span className="text-slate-500">https://</span>
                <span className="text-emerald-400 font-semibold">{rawDomain}</span>
                <span className="text-slate-400">{normalizedPath}</span>
              </>
            ) : rawDomain && !isConnected ? (
              // Pending State: Amber Lock + Adiosoft Slug URL + Status Indicator
              <>
                <Lock className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <span className="text-slate-500">https://</span>
                <span className="text-slate-300 font-medium">www.adiosoft.com</span>
                <span className="text-amber-400 font-semibold">/{slug}{normalizedPath}</span>
              </>
            ) : (
              // Standard Slug URL without custom domain
              <>
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span className="text-slate-500">https://</span>
                <span className="text-slate-300 font-medium">www.adiosoft.com</span>
                <span className="text-slate-400">/{slug}{normalizedPath}</span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0 ml-2">
            {rawDomain && !isConnected && (
              <span
                className="hidden lg:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] font-medium"
                title={`Özel alan adı (${rawDomain}) SSL kurulum aşamasında.`}
              >
                <Clock className="w-2.5 h-2.5 animate-pulse text-amber-400" />
                SSL Kuruluyor
              </span>
            )}

            <button
              type="button"
              onClick={() => {
                checkDomainLiveStatus(true);
                if (onRefresh) onRefresh();
              }}
              title="URL ve Alan Adı Durumunu Yenile"
              className="p-1 hover:text-white text-slate-500 transition rounded"
            >
              <RotateCw className={`w-3 h-3 ${isChecking ? 'animate-spin text-blue-400' : ''}`} />
            </button>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleOpenInNewTab}
            title={
              rawDomain && !isConnected
                ? 'Özel alan adınızın SSL kurulumu devam ettiği için Adiosoft bağlantısı açılacaktır'
                : 'Siteyi Yeni Sekmede Aç'
            }
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition flex items-center gap-1.5 shadow-sm active:scale-95 ${
              rawDomain && !isConnected
                ? 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border-amber-500/30'
                : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-700'
            }`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            <span>Yeni Sekmede Aç</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Önizlemeyi Kapat"
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-500/20 hover:text-rose-300 text-slate-400 border border-slate-700 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Elegant Warning / Notification Banner for Pending SSL Setup (Rule 14) */}
      {noticeMessage && (
        <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-2.5 text-amber-200 text-xs flex items-center justify-between gap-3 backdrop-blur-md animate-in slide-in-from-top-2">
          <div className="flex items-center gap-2 max-w-4xl">
            <Info className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="font-medium leading-relaxed">{noticeMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setNoticeMessage(null)}
            className="p-1 text-amber-400 hover:text-white rounded transition hover:bg-amber-500/20"
            title="Bildirimi Kapat"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};
